import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, CheckCircle, ChevronDown } from 'lucide-react';
import { setupRecaptcha, sendOTP, verifyOTP } from '../../lib/firebase';
import { checkUserExists, createMinimalUserProfile } from '../../lib/supabase';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Card, { CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { useAuth } from '../../contexts/AuthContext';
import { trackEvent, trackException } from '../../lib/analytics';

// Country codes for dropdown
const countryCodes = [
  { code: '+91', country: 'India' },
  { code: '+1', country: 'USA' },
  { code: '+44', country: 'UK' },
  { code: '+61', country: 'Australia' },
  { code: '+86', country: 'China' },
  { code: '+971', country: 'UAE' },
  { code: '+65', country: 'Singapore' },
  { code: '+81', country: 'Japan' },
];

const PhoneAuth: React.FC = () => {
  const { userProfile, setMockUser } = useAuth();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('+91'); // Default to India
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState<'phone' | 'otp' | 'new-user'>('phone');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmationResult, setConfirmationResult] = useState<any>(null);
  const [isExistingUser, setIsExistingUser] = useState(false);
  
  const recaptchaContainerRef = useRef<HTMLDivElement>(null);
  const countryDropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Track page visit
  useEffect(() => {
    trackEvent('auth_page', 'visit', 'phone_auth');
  }, []);

  // If user already has a profile with data, redirect to home
  useEffect(() => {
    if (userProfile && userProfile.name) {
      navigate('/');
    }
  }, [userProfile, navigate]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target as Node)) {
        setShowCountryDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleCheckUser = async () => {
    if (!phoneNumber.trim()) {
      setError('Please enter a valid phone number');
      trackEvent('auth_error', 'validation', 'empty_phone_number');
      return;
    }

    setLoading(true);
    setError('');
    
    trackEvent('auth_attempt', 'check_user', countryCode + phoneNumber);

    try {
      // Format the phone number with selected country code
      const formattedNumber = countryCode + phoneNumber.replace(/^0+/, '');

      // Check if user exists in database
      const userExists = await checkUserExists(formattedNumber);
      
      if (userExists) {
        // Existing user - proceed with OTP verification
        setIsExistingUser(true);
        
        // Setup reCAPTCHA and send OTP
        const recaptchaVerifier = setupRecaptcha('recaptcha-container');
        const confirmation = await sendOTP(formattedNumber, recaptchaVerifier);
        setConfirmationResult(confirmation);
        setStep('otp');
        
        trackEvent('auth_flow', 'existing_user_otp_sent', countryCode);
      } else {
        // New user - create minimal profile and auto-login
        setIsExistingUser(false);
        
        // Create minimal profile in database
        await createMinimalUserProfile(formattedNumber);
        
        // Auto-login the new user
        setMockUser(formattedNumber);
        
        trackEvent('auth_flow', 'new_user_created', countryCode);
        trackEvent('user_signup', 'new_user', 'direct_signup');
        
        // Show success message briefly, then redirect
        setStep('new-user');
        
        // Auto-redirect after 2 seconds
        setTimeout(() => {
          navigate('/profile/complete');
        }, 2000);
      }
    } catch (err: any) {
      console.error('Error checking user:', err);
      const errorMessage = err.message || 'Failed to process request. Please try again.';
      setError(errorMessage);
      
      trackEvent('auth_error', 'check_user_failed', err.code || 'unknown');
      trackException(`User check failed: ${errorMessage}`, false);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp.trim() || otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      trackEvent('auth_error', 'validation', 'invalid_otp_format');
      return;
    }

    setLoading(true);
    setError('');
    
    trackEvent('auth_attempt', 'otp_verification', 'verify_otp');

    try {
      await verifyOTP(confirmationResult, otp);
      
      trackEvent('auth_success', 'phone_verified', countryCode);
      trackEvent('user_login', 'complete', 'existing_user');
      
      // Navigate to appropriate page based on profile completion status
      navigate('/profile/complete');
    } catch (err: any) {
      console.error('Error verifying OTP:', err);
      const errorMessage = err.message || 'Invalid OTP. Please try again.';
      setError(errorMessage);
      
      trackEvent('auth_error', 'otp_verification_failed', err.code || 'unknown');
      trackException(`OTP verification failed: ${errorMessage}`, false);
    } finally {
      setLoading(false);
    }
  };

  const handleNewUserContinue = () => {
    // For new users, directly proceed to profile completion
    trackEvent('auth_flow', 'new_user_continue', 'profile_completion');
    navigate('/profile/complete');
  };

  const selectCountryCode = (code: string) => {
    setCountryCode(code);
    setShowCountryDropdown(false);
    trackEvent('auth_interaction', 'country_selected', code);
  };

  return (
    <div className="min-h-screen bg-cosmic-gradient flex flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-white mb-4">
            <img 
              src="/AI_Pandit_Ji_Logo_100x100.png" 
              alt="AI Pandit Ji" 
              className="h-16 w-16 rounded-full"
            />
          </div>
          <h1 className="text-3xl font-bold text-white font-display">AI Pandit Ji</h1>
          <p className="mt-2 text-secondary-300">Your AI-powered personal astrologer</p>
        </div>
        
        <Card className="w-full">
          <CardHeader>
            <CardTitle>
              {step === 'phone' ? 'Enter Your Phone Number' : 
               step === 'otp' ? 'Verify OTP' : 
               'Welcome to AI Pandit Ji!'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {step === 'phone' ? (
              <>
                <p className="text-sm text-neutral-600 mb-4">
                  Enter your phone number to get started with personalized astrological readings.
                </p>
                
                <div className="flex mb-4">
                  {/* Country code dropdown */}
                  <div className="relative" ref={countryDropdownRef}>
                    <button
                      type="button"
                      className="flex items-center justify-between w-24 px-3 py-2 border border-neutral-300 rounded-l-lg bg-white text-neutral-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      onClick={() => {
                        setShowCountryDropdown(!showCountryDropdown);
                        trackEvent('auth_interaction', 'country_dropdown_toggle', showCountryDropdown ? 'close' : 'open');
                      }}
                    >
                      <span>{countryCode}</span>
                      <ChevronDown size={16} className="ml-1" />
                    </button>
                    
                    {showCountryDropdown && (
                      <div className="absolute z-10 mt-1 w-48 bg-white rounded-md shadow-lg max-h-60 overflow-auto">
                        <ul className="py-1">
                          {countryCodes.map((country) => (
                            <li 
                              key={country.code}
                              className="px-3 py-2 hover:bg-primary-50 cursor-pointer flex items-center"
                              onClick={() => selectCountryCode(country.code)}
                            >
                              <span className="font-medium">{country.code}</span>
                              <span className="ml-2 text-neutral-600 text-sm">{country.country}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                  
                  {/* Phone number input */}
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => {
                      setPhoneNumber(e.target.value.replace(/\D/g, ''));
                      if (error) setError(''); // Clear error on input
                    }}
                    placeholder="9876543210"
                    className="flex-1 rounded-r-lg border border-neutral-300 px-4 py-2 text-neutral-900 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-shadow"
                    disabled={loading}
                  />
                </div>
                
                {error && (
                  <p className="text-sm text-error-500 mb-4">{error}</p>
                )}
                
                {/* Hidden reCAPTCHA container */}
                <div id="recaptcha-container" ref={recaptchaContainerRef}></div>
                
                <Button
                  onClick={handleCheckUser}
                  isLoading={loading}
                  className="mt-4"
                  fullWidth
                >
                  Continue
                </Button>
              </>
            ) : step === 'otp' ? (
              <>
                <p className="text-sm text-neutral-600 mb-4">
                  Enter the 6-digit code sent to {countryCode + phoneNumber}.
                </p>
                
                <Input
                  label="One-Time Password"
                  type="text"
                  value={otp}
                  onChange={(e) => {
                    setOtp(e.target.value.replace(/\D/g, ''));
                    if (error) setError(''); // Clear error on input
                  }}
                  placeholder="123456"
                  maxLength={6}
                  leftIcon={<CheckCircle size={18} className="text-neutral-500" />}
                  error={error}
                  fullWidth
                  disabled={loading}
                />
                
                <Button
                  onClick={handleVerifyOTP}
                  isLoading={loading}
                  className="mt-4"
                  fullWidth
                >
                  Verify OTP
                </Button>
                
                <button
                  onClick={() => {
                    setStep('phone');
                    setOtp('');
                    setError('');
                    trackEvent('auth_interaction', 'change_phone_number', 'back_button');
                  }}
                  className="mt-4 text-sm text-primary-600 hover:text-primary-700 w-full text-center"
                  disabled={loading}
                >
                  Change phone number?
                </button>
              </>
            ) : (
              <>
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                    <CheckCircle className="h-8 w-8 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                    Welcome to AI Pandit Ji!
                  </h3>
                  <p className="text-sm text-neutral-600 mb-6">
                    Your account has been created with {countryCode + phoneNumber}. Redirecting you to complete your profile for personalized readings...
                  </p>
                  
                  <div className="flex justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary-600"></div>
                  </div>
                  
                  <Button
                    onClick={handleNewUserContinue}
                    variant="outline"
                    size="sm"
                    className="mt-4"
                  >
                    Continue Now
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PhoneAuth;